/**
 * Deep Link Handling
 * Handles miac:// URL scheme and universal links
 * 
 * NOTE: OAuth authentication callbacks are handled by AuthContext (src/lib/auth/context.tsx).
 * This module only handles non-auth deep links for navigation purposes.
 * Do NOT add OAuth/auth callback handling here to avoid race conditions with
 * duplicate code exchange attempts.
 */

import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

const isNative = Capacitor.isNativePlatform()

type NavigateFunction = (path: string) => void

let navigateFunction: NavigateFunction | null = null

/**
 * Set the navigation function for handling deep links
 */
export function setDeepLinkNavigate(navigate: NavigateFunction): void {
  navigateFunction = navigate
}

/**
 * Initialize deep link listeners for non-auth deep links only.
 * Auth callbacks are handled by AuthContext to avoid race conditions.
 */
export async function initDeepLinks(): Promise<void> {
  if (!isNative) {
    console.log('Skipping deep link init - not on native platform')
    return
  }

  // Handle URL open events (deep links)
  App.addListener('appUrlOpen', async (event) => {
    console.log('Deep link received:', event.url)
    await handleDeepLink(event.url)
  })

  // Check if app was opened with a URL
  const launchUrl = await App.getLaunchUrl()
  if (launchUrl?.url) {
    console.log('App launched with URL:', launchUrl.url)
    await handleDeepLink(launchUrl.url)
  }
}

/**
 * Handle deep link URL (non-auth links only)
 * Auth callbacks are handled by AuthContext to avoid duplicate code exchange.
 */
async function handleDeepLink(url: string): Promise<void> {
  try {
    const parsedUrl = new URL(url)
    
    // For miac:// scheme, the "host" is actually part of the path
    // e.g., miac://pomodoro/start -> host: "pomodoro", pathname: "/start"
    // We need to combine them: /pomodoro/start
    let path = parsedUrl.pathname
    if (parsedUrl.protocol === 'miac:' && parsedUrl.host) {
      path = `/${parsedUrl.host}${parsedUrl.pathname}`
    }
    
    const searchParams = parsedUrl.searchParams

    console.log('Handling deep link path:', path, 'host:', parsedUrl.host, 'params:', Object.fromEntries(searchParams))

    // Skip auth callbacks - they are handled by AuthContext
    // This prevents race conditions with duplicate code exchange attempts
    if (path.includes('/auth/callback') || url.includes('auth/callback')) {
      console.log('Auth callback detected - skipping (handled by AuthContext)')
      return
    }

    // Handle other deep links by navigating
    if (navigateFunction) {
      // Map miac:// URLs to app routes
      const routePath = mapDeepLinkToRoute(path, searchParams)
      navigateFunction(routePath)
    }
  } catch (error) {
    console.error('Error handling deep link:', error)
  }
}

/**
 * Map deep link paths to app routes
 */
function mapDeepLinkToRoute(path: string, searchParams: URLSearchParams): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path : `/${path}`

  // Handle specific deep link patterns
  // miac://habit/123 -> /?habit=123 (opens drawer on dashboard)
  // miac://challenge/456 -> /challenges/456
  // miac://user/username -> /u/username
  // miac://feed -> /feed
  // etc.

  switch (true) {
    // Tracker deep link: open tracker detail on dashboard
    case cleanPath.startsWith('/tracker/'): {
      const trackerId = cleanPath.split('/')[2]
      return `/?tracker=${trackerId}`
    }

    // Widget deep link: open habit and auto-start camera for photo proof
    case cleanPath.match(/^\/habit\/[^/]+\/capture$/) !== null: {
      const habitId = cleanPath.split('/')[2]
      return `/?habit=${habitId}&autoCapture=true`
    }
    
    // Widget deep link: quick complete and navigate to habit
    case cleanPath.match(/^\/habit\/[^/]+\/complete$/) !== null: {
      const habitId = cleanPath.split('/')[2]
      return `/?habit=${habitId}&quickComplete=true`
    }
    
    // Edit habit -> dashboard with edit drawer
    case cleanPath.match(/^\/habit\/[^/]+\/edit$/) !== null: {
      const habitId = cleanPath.split('/')[2]
      return `/?editHabit=${habitId}`
    }
    
    // Habit detail -> dashboard with drawer
    case cleanPath.startsWith('/habit/'): {
      const habitId = cleanPath.split('/')[2]
      return `/?habit=${habitId}`
    }
    
    case cleanPath.startsWith('/challenge/'):
      // Map /challenge/id to /challenges/id
      return cleanPath.replace('/challenge/', '/challenges/')
    
    case cleanPath.startsWith('/user/'):
      // Map /user/username to /u/username
      return cleanPath.replace('/user/', '/u/')
    
    case cleanPath === '/home':
    case cleanPath === '/dashboard':
      return '/'
    
    // Widget deep links for pomodoro/focus timer
    // Support multiple URL patterns that widgets might use
    case cleanPath === '/pomodoro/start':
    case cleanPath === '/focus/start':
    case cleanPath === '/timer/start':
      // Navigate to pomodoro page with auto-start
      return '/pomodoro?autostart=true'
    
    // Widget deep link to stop/cancel the current pomodoro session
    case cleanPath === '/pomodoro/stop':
    case cleanPath === '/focus/stop':
    case cleanPath === '/timer/stop':
      return '/pomodoro?cancel=true'
    
    // Focus timer without auto-start (just open the page)
    case cleanPath === '/focus':
    case cleanPath === '/timer':
      return '/pomodoro'
    
    // Quick action deep link for new habit
    case cleanPath === '/habit/new':
      return '/?addHabit=true'
    
    case cleanPath === '/badges':
      return '/profile'
    
    case cleanPath === '/challenges':
    case cleanPath === '/community':
    case cleanPath === '/feed':
    case cleanPath === '/pomodoro':
    case cleanPath === '/profile':
    case cleanPath === '/settings':
    case cleanPath === '/shop':
      return cleanPath
    
    default:
      // Return as-is, or fallback to home
      return cleanPath || '/'
  }
}

/**
 * Generate a shareable deep link URL
 */
export function generateDeepLink(type: 'habit' | 'challenge' | 'user' | 'feed', id?: string): string {
  const baseUrl = isNative ? 'miac://' : 'https://miac.app'
  
  switch (type) {
    case 'habit':
      return `${baseUrl}/habit/${id}`
    case 'challenge':
      return `${baseUrl}/challenges/${id}`
    case 'user':
      return `${baseUrl}/u/${id}`
    case 'feed':
      return `${baseUrl}/feed`
    default:
      return baseUrl
  }
}

/**
 * Generate a web URL for sharing (always uses web URL for sharing)
 */
export function generateShareUrl(type: 'habit' | 'challenge' | 'user' | 'feed', id?: string): string {
  const baseUrl = 'https://miac.app'
  
  switch (type) {
    case 'habit':
      return `${baseUrl}/habit/${id}`
    case 'challenge':
      return `${baseUrl}/challenges/${id}`
    case 'user':
      return `${baseUrl}/u/${id}`
    case 'feed':
      return `${baseUrl}/feed`
    default:
      return baseUrl
  }
}
